import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { isApiError } from "@/api/client";
import { qk } from "@/api/queryKeys";
import type { Product, PurchaseResponse } from "@/api/types";
import { isBillingError, platform, type PurchaseResult } from "@/platform";
import { t } from "@/i18n/ko";
import { productName } from "@/i18n/codes";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { analytics } from "@/analytics";
import { beginPurchase, claimPurchase, endPurchase, releasePurchase } from "./purchaseClaims";
import { submitPurchase } from "./submitPurchase";

/** /store/done 라우터 state(1.15). 새로고침으로 사라지면 /store로. */
export type PurchaseDoneState = { purchase: PurchaseResponse; name: string };

/**
 * 1.15 usePurchase: purchase_start → platform.billing.purchase(productId)(dev 기본 = `mock:<productId>` 즉시) →
 * 스토어별 POST /purchases/{google|apple} → granted|already_granted → billing.finish(iOS) → balance 반영 → /store/done(router state).
 * Android consume 은 서버 몫. 검증 실패면 finish 하지 않고 남겨 복구(usePurchaseRecovery)가 다시 낸다.
 * held(지급 보류) → 안내 토스트, 완료 화면 없음. 취소 → 조용히 종료. pending → 승인 대기 토스트. PRODUCT_UNKNOWN → 토스트 + 목록 재조회. unavailable → 토스트.
 * 스토어 단계 failed → F06-03 다이얼로그(StorePage, isStoreFailure). 검증 단계 실패는 결제가 됐을 수 있어 토스트로 둔다.
 */
type PurchaseFailure = { stage: "plugin" | "verify"; error: unknown };

/** 스토어(결제 플러그인) 단계에서 실패해 청구되지 않은 경우. 취소·대기·불가는 제외 */
export const isStoreFailure = (failure: unknown): boolean => {
  const f = failure as PurchaseFailure | null;
  return f?.stage === "plugin" && isBillingError(f.error) && f.error.code === "failed";
};

export function usePurchase() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const showSnackbar = useUiStore((s) => s.showSnackbar);

  return useMutation<{ res: PurchaseResponse; product: Product }, PurchaseFailure, Product>({
    mutationFn: async (product: Product): Promise<{ res: PurchaseResponse; product: Product }> => {
      let token: PurchaseResult;
      analytics.track("start_purchase", { product_id: product.productId });
      beginPurchase();
      try {
        token = await platform.billing.purchase(product.productId);
      } catch (e) {
        if (isBillingError(e)) throw { stage: "plugin", error: e } as const;
        throw { stage: "plugin", error: { code: "failed", cause: e } } as const;
      } finally {
        endPurchase();
      }
      claimPurchase(token); // 복구가 같은 증빙을 겹쳐 내지 않게. 이미 복구가 집었어도 제출은 한다(서버 멱등, 완료 화면용 응답)
      try {
        const res = await submitPurchase(token);
        void platform.billing.finish(token).catch(() => undefined);
        return { res, product };
      } catch (e) {
        releasePurchase(token);
        throw { stage: "verify", error: e } as const;
      }
    },
    onSuccess: ({ res, product }) => {
      if (res.status === "held") {
        // 결제는 확인됐지만 지급 보류(브릭 미설정 상품 등). 스토어 처리(finish)는 끝냈고, 지급은 운영이 한다
        showSnackbar(t("store.purchase.held"));
        return;
      }
      useAuthStore.getState().setBalance(res.balance);
      void qc.invalidateQueries({ queryKey: qk.bricks });
      void qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: qk.products });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
      const state: PurchaseDoneState = { purchase: res, name: productName(product) };
      navigate("/store/done", { state });
    },
    onError: (failure) => {
      const { stage, error } = failure;
      if (stage === "plugin" && isBillingError(error)) {
        if (error.code === "cancelled" || error.code === "failed") return; // 취소는 조용히, failed 는 StorePage 다이얼로그
        showSnackbar(t(error.code === "unavailable" ? "store.purchase.unavailable" : "store.purchase.pending"));
        return;
      }
      const code = isApiError(error) ? error.code : "UNKNOWN";
      if (code === "PRODUCT_UNKNOWN") {
        showSnackbar(t("store.productUnknown"));
        void qc.invalidateQueries({ queryKey: qk.products });
        return;
      }
      showSnackbar(code === "NETWORK" ? t("error.network") : t("store.purchase.failed"));
    },
  });
}
